// src/pages/Devices/index.js
//
// Cihazlar (admin) — sistemdeki tüm ortak cihaz kayıtları.
// Düz liste (docs/design-principles.md §3): tek arama + tip filtresi → durum
// çipleri (sayılı) → çerçevesiz tablo. Satır tıklanınca drawer: bilgi listesi,
// durum değiştirme ve silme (admin-devices:manage). Web'de cihaz oluşturma yok;
// cihazlar mobil uygulamadan BLE ile tanıtılır.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { DevicesOther as DevicesIcon } from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useAlert } from "../../context/AlertProvider";
import { useClinicTheme } from "../../theme";
import {
  useLazyGetAllDevicesQuery,
  useAssignToPatientMutation,
  useAssignToServiceMutation,
  useAssignToDepotMutation,
  useDeleteDeviceMutation,
} from "../../store/deviceApi";
import { useLazyGetAllUsersQuery } from "../../store/userAccountApi";
import { FormSelect } from "../../components/atoms";
import { DataTableCard } from "../../components/organisms";
import {
  ClinicRecordListLayout,
  ClickableRow,
  DateCell,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PlainHead,
  ROWS_PER_PAGE_OPTIONS,
  SkeletonRows,
  SortHead,
  StatusPill,
  TextCell,
  cellSx,
  mainText,
  subText,
  useClientList,
} from "../../components/organisms/ClinicRecordList";
import DeviceDetailDrawer from "./DeviceDetailDrawer";
import DeviceDeleteDialog from "./DeviceDeleteDialog";
import { STATUS_OPTIONS, STATUS_TONE, TYPE_OPTIONS } from "./constants";
import {
  DEVICE_DEFAULT_DIR,
  DEVICE_SORTERS,
  matchesDevice,
  typeCodeOf,
  typeLabelOf,
} from "./deviceListModel";

const COLS = 5;
const MONO = { fontFamily: "'SF Mono', 'Fira Code', monospace" };
const TYPE_KEY = "devicesList:type";

const statusOf = (d) => d.statusName || "—";

const errorText = (e, fallback) => e?.data?.message || fallback;

function readType() {
  try {
    return sessionStorage.getItem(TYPE_KEY) || "";
  } catch (_) {
    return "";
  }
}

export default function Devices() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();
  const canManage = useHasPermission("admin-devices:manage");

  const [getAllDevices, { data, isFetching, isError }] =
    useLazyGetAllDevicesQuery();
  const [assignToPatient, { isLoading: assigningPatient }] =
    useAssignToPatientMutation();
  const [assignToService, { isLoading: assigningService }] =
    useAssignToServiceMutation();
  const [assignToDepot, { isLoading: assigningDepot }] =
    useAssignToDepotMutation();
  const [deleteDevice, { isLoading: isDeleting }] = useDeleteDeviceMutation();
  const [getAllUsers, { data: usersData }] = useLazyGetAllUsersQuery();

  const [type, setTypeRaw] = useState(readType);
  const [selected, setSelected] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const refresh = useCallback(() => {
    if (token)
      getAllDevices({ token })
        .unwrap()
        .catch(() => {});
  }, [token, getAllDevices]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const allRows = useMemo(() => data?.data?.devices || [], [data]);
  const loaded = Boolean(data) || isError;

  // Drawer açıkken liste yenilenirse seçili kaydı güncel haliyle değiştir
  useEffect(() => {
    if (!selected) return;
    const fresh = allRows.find((d) => d._id === selected._id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [allRows, selected]);

  // Tip filtresi aramadan önce uygulanır; durum çiplerinin sayıları seçili tipe göre
  const typedRows = useMemo(
    () => (type ? allRows.filter((d) => typeCodeOf(d) === type) : allRows),
    [allRows, type],
  );

  const list = useClientList(typedRows, {
    segmentOf: statusOf,
    matches: matchesDevice,
    sorters: DEVICE_SORTERS,
    defaultDir: DEVICE_DEFAULT_DIR,
    initialSort: "date",
    persistKey: "devicesList:view",
  });

  const setType = (v) => {
    setTypeRaw(v);
    try {
      sessionStorage.setItem(TYPE_KEY, v);
    } catch (_) {
      // kalıcılık best-effort
    }
    list.onPageChange(null, 0);
  };

  const segments = useMemo(
    () => [
      { value: "all", label: `Tümü · ${list.counts.all || 0}` },
      ...STATUS_OPTIONS.map((o) => ({
        value: o.value,
        label: `${o.label} · ${list.counts[o.value] || 0}`,
      })),
    ],
    [list.counts],
  );

  const patients = useMemo(
    () => (usersData?.data?.users || []).filter((u) => u.role === "Patient"),
    [usersData],
  );
  const loadPatients = useCallback(() => {
    if (token && !usersData) getAllUsers({ token });
  }, [token, usersData, getAllUsers]);

  const submitStatus = async (device, status, patientId) => {
    const base = { token, deviceId: device._id };
    try {
      if (status === "Hastada")
        await assignToPatient({ ...base, patientId }).unwrap();
      else if (status === "Servis") await assignToService(base).unwrap();
      else await assignToDepot(base).unwrap();
      showSuccess("Cihaz durumu güncellendi.");
      refresh();
      return true;
    } catch (e) {
      showError(errorText(e, "Durum güncellenemedi."));
      return false;
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteDevice({ token, deviceId: toDelete._id }).unwrap();
      showSuccess("Cihaz silindi.");
      setToDelete(null);
      setSelected(null);
      refresh();
    } catch (e) {
      showError(errorText(e, "Cihaz silinemedi."));
    }
  };

  const busy = isFetching && !loaded;
  const empty = isError
    ? {
        title: "Cihazlar yüklenemedi",
        message: "Bağlantınızı kontrol edip sayfayı yenileyin.",
      }
    : list.searchText
      ? {
          title: "Cihaz bulunamadı",
          message: `“${list.searchText}” aramanızla eşleşen cihaz yok. Aramayı değiştirin.`,
        }
      : list.segment !== "all" || type
        ? {
            title: "Bu filtrede cihaz yok",
            message:
              "Seçili tip ve durumda cihaz bulunmuyor. Filtreyi değiştirin.",
          }
        : {
            title: "Henüz cihaz yok",
            message:
              "Cihazlar mobil uygulamadan admin girişiyle tanıtıldığında burada listelenir.",
          };

  const sortProps = { sort: list.sort, dir: list.dir, onSort: list.onSort };

  return (
    <>
      <ClinicRecordListLayout
        icon={<DevicesIcon sx={{ fontSize: 22 }} />}
        title="Cihazlar"
        subtitle="Sisteme tanıtılmış tüm ölçüm cihazları"
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder="Seri no, MAC, hasta adı veya e-posta ile ara"
        filters={
          <FormSelect
            palette={palette}
            value={type}
            onChange={setType}
            options={TYPE_OPTIONS}
            placeholder={null}
            fullWidth={false}
            SelectProps={{ displayEmpty: true }}
            sx={{ width: { xs: "100%", sm: 220 } }}
            inputProps={{ "aria-label": "Cihaz tipi" }}
          />
        }
        segments={segments}
        segment={list.segment}
        onSegmentChange={list.setSegment}
        segmentAriaLabel="Cihaz durumu"
      >
        <DataTableCard
          loading={Boolean(busy)}
          variant="flat"
          empty={!busy && list.filtered.length === 0}
          emptyIcon={<DevicesIcon />}
          emptyTitle={empty.title}
          emptyMessage={empty.message}
          page={list.page}
          rowsPerPage={list.rowsPerPage}
          totalCount={list.filtered.length}
          onPageChange={list.onPageChange}
          onRowsPerPageChange={list.onRowsPerPageChange}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        >
          <Table
            stickyHeader
            size="small"
            sx={{ tableLayout: "fixed", minWidth: 960 }}
          >
            <TableHead>
              <TableRow>
                <SortHead
                  field="serial"
                  label="Cihaz"
                  {...sortProps}
                  sx={{ ...EDGE_L, width: "22%" }}
                />
                <PlainHead sx={{ width: "22%" }}>Tip</PlainHead>
                <SortHead
                  field="patient"
                  label="Hasta"
                  {...sortProps}
                  sx={{ width: "28%" }}
                />
                <SortHead
                  field="date"
                  label="Tanıtılma"
                  defaultDir="desc"
                  {...sortProps}
                  sx={{ width: "14%" }}
                />
                <PlainHead sx={{ ...EDGE_R, width: "14%" }}>Durum</PlainHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {busy ? (
                <SkeletonRows
                  rows={Math.min(10, list.rowsPerPage)}
                  cols={COLS}
                />
              ) : list.pagedRows.length ? (
                list.pagedRows.map((row) => (
                  <ClickableRow
                    key={row._id}
                    onClick={() => setSelected(row)}
                    palette={palette}
                  >
                    <TableCell sx={{ ...cellSx, ...EDGE_L }}>
                      <Typography
                        sx={{ ...mainText, ...MONO, fontWeight: 600 }}
                        noWrap
                      >
                        {row.serialNo || "—"}
                      </Typography>
                      <Typography sx={{ ...subText, ...MONO }} noWrap>
                        {row.mac || "—"}
                      </Typography>
                    </TableCell>
                    <TextCell
                      main={typeLabelOf(row)}
                      sub={row.modelLabel || null}
                    />
                    <TextCell
                      main={
                        row.patient
                          ? `${row.patient.name || ""} ${row.patient.surname || ""}`.trim()
                          : null
                      }
                      sub={row.patient?.email || null}
                    />
                    <DateCell value={row.provisionedAt} />
                    <TableCell sx={{ ...cellSx, ...EDGE_R }}>
                      <StatusPill
                        label={row.statusName || "—"}
                        tone={STATUS_TONE[row.statusName]}
                        palette={palette}
                      />
                    </TableCell>
                  </ClickableRow>
                ))
              ) : (
                <FillerRow cols={COLS} />
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </ClinicRecordListLayout>

      <DeviceDetailDrawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        device={selected}
        canManage={canManage}
        patients={patients}
        onLoadPatients={loadPatients}
        onSubmitStatus={submitStatus}
        isSubmitting={assigningPatient || assigningService || assigningDepot}
        onDelete={setToDelete}
      />

      <DeviceDeleteDialog
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        device={toDelete}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </>
  );
}
